import { useCallback } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getHashedLoomVideoIdFromUrl } from '@atlassian/analytics-data-privacy';

import {
	useLoomCrossJoinUrl,
	useLoomEntryPointVariant,
	useLoomRecorderEntryPoint,
} from '@confluence/loom-utils';
import type { FlagsStateContainer } from '@confluence/flags';
import { useSpaceId } from '@confluence/space-utils';

import { type IntegrationTriggerOptions } from './integration-service-types';

type OpenLoomRecorderArgs = {
	insertButtonText: string;
	onSuccess: (url: string) => void;
};

export const useLoomService = (
	flags: FlagsStateContainer,
	contentId: string,
	contentStatus: string | null,
) => {
	const entryPointVariant = useLoomEntryPointVariant();
	const isEntryPointEnabled = ['BETA', 'CO_USE', 'CROSS_JOIN'].includes(entryPointVariant);
	const crossJoinUrl = useLoomCrossJoinUrl();

	const { openLoomRecorder: localOpenLoom } = useLoomRecorderEntryPoint({
		flags,
		entryPointLocation: 'whiteboards',
		resetInsertFunctionOnUnmount: true,
	});
	const spaceId = useSpaceId();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isAvailable = () => isEntryPointEnabled && !!localOpenLoom && contentStatus === 'current';

	const openLoomRecorder = useCallback(
		({ insertButtonText, onSuccess }: OpenLoomRecorderArgs) => {
			const onLoomInsert = (videoUrl: string) => {
				onSuccess(videoUrl);
				void getHashedLoomVideoIdFromUrl(videoUrl).then((hashedVideoId) => {
					createAnalyticsEvent({
						type: 'sendTrackEvent',
						data: {
							action: 'insertVideo',
							actionSubject: 'loom',
							source: 'whiteboardSideBar',
							containerType: 'space',
							containerId: spaceId, // spaceKey can have UGC so use spaceId
							objectType: 'whiteboard',
							objectId: contentId,
							attributes: {
								video_id: hashedVideoId,
							},
						},
					}).fire();
				});
			};

			return localOpenLoom({ insertButtonText, onInsert: onLoomInsert });
		},
		[contentId, createAnalyticsEvent, localOpenLoom, spaceId],
	);

	const triggerIntegration = useCallback(
		async ({ onSuccess, otherProps }: IntegrationTriggerOptions) => {
			if (entryPointVariant === 'CROSS_JOIN') {
				window.open(crossJoinUrl, '_blank');
				// `onSuccess` intentionally not called as it's used in Whiteboards app to insert a Loom
				// video after successful recording and not meant to handle any cross-join logic
				return;
			}

			if (entryPointVariant === 'BETA' || entryPointVariant === 'CO_USE') {
				return openLoomRecorder({ onSuccess, insertButtonText: otherProps.insertButtonText });
			}
		},
		[crossJoinUrl, entryPointVariant, openLoomRecorder],
	);

	return { triggerIntegration, isAvailable };
};

export type loomService = ReturnType<typeof useLoomService>;
